import { useState, useEffect, useCallback } from 'preact/hooks';
import { AuthProvider, useAuth } from './auth/context';
import { LoginPage } from './auth/login';
import { Shell } from './layout/shell';
import { useRoute } from './hooks/use-route';
import { CommandPalette } from './components/command-palette';
import { ToastProvider } from './components/toast';
import { OverviewTab } from './tabs/overview/overview-tab';
import { ProductsTab } from './tabs/products/products-tab';
import { IngredientsTab } from './tabs/ingredients/ingredients-tab';
import { UsersTab } from './tabs/users/users-tab';
import { VerdictsTab } from './tabs/verdicts/verdicts-tab';
import { TipsTab } from './tabs/tips/tips-tab';
import { SystemTab } from './tabs/system/system-tab';
import { SchemaTab } from './tabs/schema/schema-tab';
import { VersionTab } from './tabs/version/version-tab';

function Dashboard() {
  const { route, navigate, breadcrumbs, tabId } = useRoute();
  const [cmdPaletteOpen, setCmdPaletteOpen] = useState(false);

  // Cmd+K / Ctrl+K listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleNavigate = useCallback((hash: string) => {
    navigate(hash);
  }, [navigate]);

  const handleCommandNavigate = useCallback((tab: string) => {
    // Command palette still uses old-style tab IDs — map to new hashes
    const legacyMap: Record<string, string> = {
      overview: 'overview',
      products: 'catalog/products',
      ingredients: 'catalog/ingredients',
      users: 'catalog/users',
      verdicts: 'catalog/verdicts',
      tips: 'catalog/tips',
      health: 'ops/health',
      logs: 'ops/logs',
      cache: 'ops/cache',
      schema: 'ops/schema',
      version: 'ops/version',
      system: 'ops/health',
    };
    navigate(legacyMap[tab] ?? tab);
    setCmdPaletteOpen(false);
  }, [navigate]);

  const renderTab = () => {
    switch (tabId) {
      case 'overview':
        return <OverviewTab key="overview" onNavigate={handleNavigate} />;
      case 'products':
        return <ProductsTab key="products" />;
      case 'ingredients':
        return <IngredientsTab key="ingredients" />;
      case 'users':
        return <UsersTab key="users" />;
      case 'verdicts':
        return <VerdictsTab key="verdicts" />;
      case 'tips':
        return <TipsTab key="tips" />;
      case 'cache':
      case 'logs':
      case 'health':
      case 'system':
        return <SystemTab key={`system-${tabId}`} initialSubTab={tabId === 'cache' ? 'cache' : tabId === 'logs' ? 'logs' : 'health'} />;
      case 'schema':
        return <SchemaTab key="schema" />;
      case 'version':
        return <VersionTab key="version" />;
    }
  };

  return (
    <>
      <Shell
        route={route}
        breadcrumbs={breadcrumbs}
        onNavigate={handleNavigate}
        onOpenCommandPalette={() => setCmdPaletteOpen(true)}
        tabKey={tabId}
      >
        {renderTab()}
      </Shell>
      <CommandPalette
        open={cmdPaletteOpen}
        onClose={() => setCmdPaletteOpen(false)}
        onNavigate={(tab) => {
          handleCommandNavigate(tab);
        }}
      />
    </>
  );
}

function AppInner() {
  const { secret } = useAuth();

  if (!secret) {
    return <LoginPage />;
  }

  return <Dashboard />;
}

export function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppInner />
      </ToastProvider>
    </AuthProvider>
  );
}
